import React, { useEffect, useState, useMemo, useCallback } from 'react';
import CustomDialog from 'components/CustomDialog/CustomDialog';
import { translate } from 'components/Translation';
import { makeStyles } from '@material-ui/core';
import styles from 'assets/jss/material-dashboard-pro-react/views/users/detailsStyle';
import ConditionalRender from 'components/ConditionalRender/ConditionalRender';
import { BodyS, Caption } from 'components/Typography/Typography';
import CustomSearch, { ISearchElement } from 'components/CustomSearch/CustomSearch';
import { BillsService } from 'services/bills/bills';
import { finalize } from 'rxjs/operators';
import { SingleBillId } from 'utils/api/models/bills/singleBillId';
import DatePicker from './subcomponents/DatePicker';
import classNames from 'classnames';
import Close from '@material-ui/icons/Close';

// @ts-ignore
const useStyles = makeStyles(styles);

interface IAddPermissionProps {
	hideDialog: () => void;
	handleSaveData: (permissions: types.users.IExtendedSinglePermission[]) => void;
	handleUpdateData: (permissions: types.users.IExtendedSinglePermission[]) => void;
	persistedPermissions: types.users.IExtendedSinglePermission[];
}

type Expiration = {
	billingId: string;
	isUnlimited: boolean;
	date: string;
};

const AddBillingAccountPermission = ({
	hideDialog,
	handleSaveData,
	handleUpdateData,
	persistedPermissions,
}: IAddPermissionProps) => {
	const classes = useStyles();

	const [billingIds, setBillingIds] = useState<SingleBillId[]>([]);
	const [selectedBillingIds, setSelectedBillingIds] = useState<string[]>([]);
	const [expirations, setExpirations] = useState<Expiration[]>([]);
	const [loading, setLoading] = useState(false);

	const persistedBillingAccountPermissions = useMemo(() => {
		return persistedPermissions.filter((permission) => permission.type === 'B');
	}, [persistedPermissions]);

	const isPermissionsEditMode = persistedBillingAccountPermissions.length > 0;

	useEffect(() => {
		setSelectedBillingIds(
			persistedBillingAccountPermissions
				.filter((permission) => permission.billingAccountId)
				.map((permission) => permission.billingAccountId as string)
		);

		setExpirations(
			persistedBillingAccountPermissions.map((permission) => {
				const isUnlimited = !permission.permissionExpiration;
				const date = isUnlimited ? '' : permission.permissionExpiration;
				return { billingId: permission.billingAccountId as string, isUnlimited, date: date as string };
			})
		);

		setLoading(true);
		const subscription = BillsService.getCorpBillingIds()
			.pipe(finalize(() => setLoading(false)))
			.subscribe(({ billingIds }) => {
				setBillingIds(billingIds);
			});
		return () => {
			subscription.unsubscribe();
		};
	}, [persistedBillingAccountPermissions]);

	const mappedBillingIdNumbers = useMemo(() => {
		return [...new Set(billingIds.map((billId) => billId.billingId))]
			.filter((billingId) => {
				return !selectedBillingIds.includes(billingId);
			})
			.map((billingId) => ({
				id: billingId,
				value: billingId,
				content: billingId,
			}));
	}, [billingIds, selectedBillingIds]);

	const onSuggestionClick = (suggestion: ISearchElement | null) => {
		if (suggestion) {
			setSelectedBillingIds((billingIds) => billingIds.concat([suggestion.value]));
			addEmptyExpirationToState(suggestion.value);
		}
	};

	const deletePermission = (billingId: string) => {
		setSelectedBillingIds((billingIds) => {
			return billingIds.filter((stateBillingId) => {
				return stateBillingId !== billingId;
			});
		});
		setExpirations((expirations) => expirations.filter((expiration) => expiration.billingId !== billingId));
	};

	const addEmptyExpirationToState = (billingId: string) => {
		const newExpiration = { billingId, isUnlimited: true, date: '' };
		setExpirations((expirations) => expirations.concat([newExpiration]));
		return newExpiration;
	};

	const resetData = () => {
		setSelectedBillingIds([]);
		setExpirations([]);
	};

	const expiration = (billingId: string) => {
		let match = expirations.find((expiration) => expiration.billingId === billingId);
		if (!match) {
			match = addEmptyExpirationToState(billingId);
		}
		return match;
	};

	const isExpirationValid = useCallback((expiration: Expiration) => {
		return expiration.date !== '' || expiration.isUnlimited;
	}, []);

	const isFormValid = useMemo(() => {
		return (
			expirations.filter((expiration) => {
				return !isExpirationValid(expiration);
			}).length === 0
		);
	}, [expirations, isExpirationValid]);

	const isExpirationValidForBillingId = (billingId: string) => {
		const expiration = expirations.find((expiration) => expiration.billingId === billingId);
		if (expiration) {
			return isExpirationValid(expiration);
		}
		return true;
	};

	const prepareDataAndSave = () => {
		const billingPermissions = selectedBillingIds.map((billingId) => {
			const matchedSingleBillId = billingIds.find((billId) => billId.billingId === billingId) as SingleBillId;
			const matchedExpiration = expiration(billingId);
			const permissionExpiration = matchedExpiration.isUnlimited ? undefined : matchedExpiration.date;
			return {
				type: 'B',
				billingAccountId: billingId,
				accountId: matchedSingleBillId.accountId,
				permissionExpiration,
			} as types.users.IExtendedSinglePermission;
		});

		if (isPermissionsEditMode) {
			const persistedOtherPermissions = persistedPermissions.filter((permission) => permission.type !== 'B');
			handleUpdateData(billingPermissions.concat(persistedOtherPermissions));
		} else {
			handleSaveData(billingPermissions);
		}
	};

	const handleSetDate = (billingId: string, value: string) => {
		setExpirations((expirations) => {
			return expirations.map((expiration) => {
				if (expiration.billingId === billingId) {
					if (value === 'unlimited') {
						return {
							...expiration,
							isUnlimited: !expiration.isUnlimited,
							date: '',
						};
					} else {
						return {
							...expiration,
							isUnlimited: false,
							date: value,
						};
					}
				}
				return expiration;
			});
		});
	};

	const badgeHeader = (
		<div className={classes.permissionCellHeader}>
			<div className={classes.permissionCell}>
				<Caption color="secondary-light">{translate('users.permissions.billing.account')}</Caption>
			</div>
			<div className={classes.permissionCell}>
				<Caption color="secondary-light">{translate('access.expiration.date')}</Caption>
			</div>
			<div className={classes.permissionCloseCell}></div>
		</div>
	);

	return (
		<CustomDialog
			isOpen
			hideDialog={() => hideDialog()}
			header={
				isPermissionsEditMode
					? translate('users.details.editPermissions')
					: translate('users.details.addPermissions')
			}
			dialogBody={
				<div className={classes.dialogBodyWrapper}>
					<CustomSearch
						elements={mappedBillingIdNumbers}
						id="billingIdSearch"
						label={''}
						placeholder={translate('users.permissions.billing.account.create.user.select')}
						elementSelected={onSuggestionClick}
						selectedElement={null}
						loading={loading}
					/>
					<div>
						<ConditionalRender show={selectedBillingIds.length > 0} onTrue={() => badgeHeader} />

						{selectedBillingIds.map((billingId) => (
							<div>
								<div key={billingId} className={classes.billingAccountPermissionContainer}>
									<div
										className={classNames(classes.permissionCell, classes.departmentBadgeContainer)}
									>
										<BodyS>{billingId}</BodyS>
									</div>
									<div className={classes.permissionCell}>
										<DatePicker
											disabled={false}
											isUnlimited={expiration(billingId).isUnlimited}
											date={expiration(billingId).date}
											setDate={(value) => handleSetDate(billingId, value)}
											isCalendarRight
											errorText={translate('required.field')}
											error={!isExpirationValidForBillingId(billingId)}
										/>
									</div>
									<div className={classes.billingAccountDeletePermission}>
										<Close onClick={() => deletePermission(billingId)} color={'error'} />
									</div>
								</div>
								<div>
									<div className={classes.departmentPermissionDivider} />
								</div>
							</div>
						))}
					</div>
				</div>
			}
			rightSideButtons={[
				{
					text: translate('cancel'),
					color: 'tertiary-light',
					onClick: () => {
						resetData();
						hideDialog();
					},
				},
				{
					text: isPermissionsEditMode ? translate('save') : translate('add'),
					color: 'primary-light',
					disabled: !isFormValid,
					onClick: () => {
						isFormValid && prepareDataAndSave();
						// setShowIsValid(true);
						// resetData();
					},
				},
			]}
		/>
	);
};

export default AddBillingAccountPermission;
